/**
 * GSC (Google Search Console) API service
 */

import apiClient from './client';
import type {
  GSCLinkStatus,
  GSCIntegrationSettings,
  GSCIntegrationSettingsUpdate,
  KeywordWithOrganic,
  SearchTermWithOrganic,
  OptimizationOpportunity,
  ImportStatus,
  GSCProperty,
  CampaignOrganicMetrics,
} from '@/types/gsc';

const GSC_BASE = '/gsc';

// === OAuth Flow ===

/**
 * Start GSC OAuth flow
 */
export async function startOAuth(): Promise<{ authorization_url: string; state: string }> {
  const response = await apiClient.get(`${GSC_BASE}/oauth/start`);
  return response.data;
}

/**
 * List GSC properties (sites) after OAuth
 */
export async function listProperties(): Promise<GSCProperty[]> {
  const response = await apiClient.get(`${GSC_BASE}/properties`);
  return response.data;
}

/**
 * Select a GSC property to connect
 */
export async function selectProperty(siteUrl: string): Promise<{ status: string; message: string; site_url: string }> {
  const response = await apiClient.post(`${GSC_BASE}/properties/select`, { site_url: siteUrl });
  return response.data;
}

/**
 * Get GSC connection status
 */
export async function getConnectionStatus(): Promise<{
  oauth_completed: boolean;
  site_url: string | null;
  enabled: boolean;
  has_data: boolean;
}> {
  const response = await apiClient.get(`${GSC_BASE}/connection-status`);
  return response.data;
}

/**
 * Get campaign-level organic metrics for Performance Metrics display
 */
export async function getCampaignOrganicMetrics(campaignId: string): Promise<CampaignOrganicMetrics> {
  const response = await apiClient.get(`${GSC_BASE}/campaign-metrics/${campaignId}`);
  return response.data;
}

/**
 * Check if Google Ads account is linked to Search Console (legacy)
 */
export async function checkLinkStatus(customerId: string): Promise<GSCLinkStatus> {
  const response = await apiClient.get(`${GSC_BASE}/link-status`, {
    params: { customer_id: customerId },
  });
  return response.data;
}

/**
 * Get GSC integration settings
 */
export async function getSettings(): Promise<GSCIntegrationSettings> {
  const response = await apiClient.get(`${GSC_BASE}/settings`);
  return response.data;
}

/**
 * Update GSC integration settings
 */
export async function updateSettings(
  settings: GSCIntegrationSettingsUpdate
): Promise<GSCIntegrationSettings> {
  const response = await apiClient.put(`${GSC_BASE}/settings`, settings);
  return response.data;
}

/**
 * Enable GSC integration (Path 1: requires Google Ads customer ID)
 */
export async function enableIntegration(customerId: string): Promise<{
  status: string;
  message: string;
  backfill_started: boolean;
}> {
  const response = await apiClient.post(`${GSC_BASE}/enable`, {
    customer_id: customerId,
  });
  return response.data;
}

/**
 * Enable GSC integration directly (Path 2: uses OAuth site_url, no customer ID required)
 */
export async function enableIntegrationDirect(): Promise<{
  status: string;
  message: string;
  backfill_started: boolean;
}> {
  const response = await apiClient.post(`${GSC_BASE}/enable-direct`);
  return response.data;
}

/**
 * Disable GSC integration
 */
export async function disableIntegration(): Promise<{
  status: string;
  message: string;
}> {
  const response = await apiClient.post(`${GSC_BASE}/disable`);
  return response.data;
}

/**
 * Get keyword metrics with organic data
 */
export async function getKeywordMetrics(params?: {
  campaign_id?: string;
  ad_group_id?: string;
  days?: number;
}): Promise<KeywordWithOrganic[]> {
  const response = await apiClient.get(`${GSC_BASE}/keyword-metrics`, { params });
  return response.data;
}

/**
 * Get search terms with organic data
 */
export async function getSearchTerms(params?: {
  campaign_id?: string;
  limit?: number;
  days?: number;
}): Promise<SearchTermWithOrganic[]> {
  const response = await apiClient.get(`${GSC_BASE}/search-terms`, { params });
  return response.data;
}

/**
 * Get optimization opportunities
 */
export async function getOpportunities(): Promise<OptimizationOpportunity[]> {
  const response = await apiClient.get(`${GSC_BASE}/opportunities`);
  return response.data;
}

/**
 * Get import status
 */
export async function getImportStatus(): Promise<ImportStatus[]> {
  const response = await apiClient.get(`${GSC_BASE}/import-status`);
  return response.data;
}

/**
 * Run historical backfill
 */
export async function runBackfill(
  customerId: string,
  days: number = 90
): Promise<{
  success: boolean;
  total_rows: number;
  chunks_processed: number;
  mapping_rows: number;
  errors: string[];
}> {
  const response = await apiClient.post(`${GSC_BASE}/backfill`, { days }, {
    params: { customer_id: customerId },
  });
  return response.data;
}

export const gscApi = {
  // OAuth Flow (Path 2)
  startOAuth,
  listProperties,
  selectProperty,
  getConnectionStatus,
  getCampaignOrganicMetrics,
  // Legacy linkage check (Path 1)
  checkLinkStatus,
  // Settings
  getSettings,
  updateSettings,
  // Enable/Disable
  enableIntegration,        // Path 1: requires customerId
  enableIntegrationDirect,  // Path 2: uses OAuth site_url
  disableIntegration,
  // Data
  getKeywordMetrics,
  getSearchTerms,
  getOpportunities,
  getImportStatus,
  runBackfill,
};

export default gscApi;
